import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { useParams } from 'react-router-dom';
import LineLoginButton from './LineLoginButton';
import EnvTest from '../EnvTest';

const LoginDebugPage = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { merchant } = useMerchant();
  const { merchantSlug } = useParams();
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    // 收集所有調試資訊
    const debugData = {
      // 環境變數
      environment: {
        NODE_ENV: process.env.NODE_ENV,
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        REACT_APP_LINE_CLIENT_ID: process.env.REACT_APP_LINE_CLIENT_ID,
        REACT_APP_LINE_FRONTEND_URL: process.env.REACT_APP_LINE_FRONTEND_URL,
        LINE_CLIENT_ID_STATUS: process.env.REACT_APP_LINE_CLIENT_ID ? 'SET' : 'NOT_SET'
      },

      // URL 資訊
      url: {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        href: window.location.href
      },

      // 認證狀態
      auth: {
        isAuthenticated: isAuthenticated,
        loading: loading,
        user: user ? {
          id: user.id,
          display_name: user.display_name,
          has_line_profile: !!user.line_profile
        } : null
      },

      // 商戶資訊
      merchant: merchant ? {
        id: merchant.id,
        name: merchant.name,
        slug: merchant.slug
      } : null,

      // localStorage 檢查
      localStorage: {
        token: localStorage.getItem('kakabooking_token') ? 'EXISTS' : 'NOT_EXISTS',
        user: localStorage.getItem('kakabooking_user') ? 'EXISTS' : 'NOT_EXISTS',
        membership: localStorage.getItem('kakabooking_membership') ? 'EXISTS' : 'NOT_EXISTS'
      },

      // 時間戳
      timestamp: new Date().toISOString()
    };

    setDebugInfo(debugData);
    console.log('🔍 Complete Debug Info:', debugData);
  }, [isAuthenticated, loading, user, merchant, merchantSlug]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            🔍 LINE Login Debug Page
          </h1>

          {/* 快速狀態檢查 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${debugInfo.environment?.LINE_CLIENT_ID_STATUS === 'SET' ? 'bg-green-100' : 'bg-red-100'}`}>
              <h3 className="font-semibold">LINE Client ID</h3>
              <p className={debugInfo.environment?.LINE_CLIENT_ID_STATUS === 'SET' ? 'text-green-800' : 'text-red-800'}>
                {debugInfo.environment?.LINE_CLIENT_ID_STATUS || 'CHECKING...'}
              </p>
            </div>

            <div className={`p-4 rounded-lg ${debugInfo.auth?.isAuthenticated ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <h3 className="font-semibold">認證狀態</h3>
              <p className={debugInfo.auth?.isAuthenticated ? 'text-green-800' : 'text-yellow-800'}>
                {debugInfo.auth?.isAuthenticated ? '已登入' : '未登入'}
              </p>
            </div>

            <div className={`p-4 rounded-lg ${debugInfo.merchant ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <h3 className="font-semibold">商戶資訊</h3>
              <p className={debugInfo.merchant ? 'text-green-800' : 'text-yellow-800'}>
                {debugInfo.merchant?.name || '載入中...'}
              </p>
            </div>
          </div>

          {/* LINE URL 調試 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">LINE 授權 URL 調試</h3>
            <div className="space-y-2 text-sm">
              <div>
                <strong>計算出的 redirect_uri:</strong>
                <div className="mt-1 p-2 bg-blue-100 rounded text-xs break-all">
                  {(() => {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://192.168.1.112:8040/api';
                    if (apiUrl.startsWith('http')) {
                      return `${apiUrl}/auth/line/callback/`;
                    } else {
                      const frontendUrl = process.env.REACT_APP_LINE_FRONTEND_URL || 'http://192.168.1.112:3000';
                      return `${frontendUrl}${apiUrl}/auth/line/callback/`;
                    }
                  })()}
                </div>
              </div>
              <div>
                <strong>完整的 LINE 授權 URL:</strong>
                <div className="mt-1 p-2 bg-blue-100 rounded text-xs break-all">
                  {(() => {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://192.168.1.112:8040/api';
                    let redirectUri;
                    if (apiUrl.startsWith('http')) {
                      redirectUri = `${apiUrl}/auth/line/callback/`;
                    } else {
                      const frontendUrl = process.env.REACT_APP_LINE_FRONTEND_URL || 'http://192.168.1.112:3000';
                      redirectUri = `${frontendUrl}${apiUrl}/auth/line/callback/`;
                    }
                    const params = new URLSearchParams({
                      response_type: 'code',
                      client_id: process.env.REACT_APP_LINE_CLIENT_ID || '',
                      redirect_uri: redirectUri,
                      state: 'kaka-booking',
                      scope: 'openid profile'
                    });
                    return `https://access.line.me/oauth2/v2.1/authorize?${params.toString()}`;
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* 環境變數測試 */}
          <EnvTest />

          {/* LINE 登入按鈕測試 */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-3">LINE 登入測試</h3>
            <LineLoginButton
              merchantSlug={merchantSlug}
              size="large"
              className="w-full max-w-md"
              onError={(error) => {
                console.error('LINE Login Error:', error);
                alert(`LINE 登入錯誤: ${error.message}`);
              }}
            />
          </div>

          {/* 詳細調試資訊 */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">詳細調試資訊</h2>

            {Object.entries(debugInfo).map(([section, data]) => (
              <div key={section} className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-2 capitalize">
                  {section.replace(/([A-Z])/g, ' $1').trim()}
                </h3>
                <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </div>
            ))}
          </div>

          {/* 操作按鈕 */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              清除 localStorage 並重新載入
            </button>

            <button
              onClick={() => {
                console.log('Current Debug Info:', debugInfo);
                navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
                alert('調試資訊已複製到剪貼板');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              複製調試資訊
            </button>

            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              重新載入頁面
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginDebugPage;